import type { ReactElement } from 'react';
import { useRef } from 'react';
import { Box, Container, Grid, Stack, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type { MarkdownRemarkFrontmatterSolutionCards, Maybe } from '../../../generated/types';
import type { PpMarkdownImageWithAlt } from '../../images/types';
import { MotionStack } from '../../components/animated/MotionStack';
import { CustomCarousel } from '../../components/carousel/CustomCarousel';
import { useBreakpoints } from '../../components/hooks/useBreakpoints';
import { useScrollAnimation } from '../../components/hooks/useScrollAnimation';
import { TextWithNoahLogo } from '../../components/typography/TextWithNoahLogo';
import { HomepageSmallCard } from './HomepageSmallCard';

type PpSolutionSection = {
  cards: MarkdownRemarkFrontmatterSolutionCards[];
  imageLarge: PpMarkdownImageWithAlt | undefined;
  imageSmall: PpMarkdownImageWithAlt | undefined;
  subtitle: Maybe<string> | undefined;
  textWithIconLabel: Maybe<string> | undefined;
  title: Maybe<string> | undefined;
};

const StyledSection = styled('section')(({ theme }) => ({
  backgroundColor: theme.palette.websiteBrand.greyscale.lighterGrey,
  marginTop: theme.spacing(10),
}));

const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(12),
  maxWidth: theme.breakpoints.values.lg,
  padding: theme.spacing(12, 0),
  [theme.breakpoints.down('sm')]: {
    marginTop: theme.spacing(6),
    padding: theme.spacing(6, 3),
  },
}));

const StyledImage = styled('img')(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(-20),
  width: '100%',
  zIndex: 1,
  [theme.breakpoints.down('lg')]: {
    top: theme.spacing(-12.5),
  },
  [theme.breakpoints.down('md')]: {
    display: 'block',
    margin: '0 auto',
    maxWidth: '300px',
    position: 'relative',
    top: theme.spacing(-15),
  },
}));

export function SolutionSection({
  cards,
  imageLarge,
  imageSmall,
  subtitle,
  textWithIconLabel,
  title,
}: PpSolutionSection): ReactElement {
  const { isSmall } = useBreakpoints();
  const containerRef = useRef(null);
  const { opacity, transform } = useScrollAnimation(containerRef, {
    opacity: [0, 1],
    scrollThreshold: 0.5,
    transform: ['translateY(-200px)', 'translateY(0px)'],
  });

  const image = isSmall ? imageSmall : imageLarge;

  return (
    <StyledSection>
      <StyledContainer disableGutters fixed>
        <Stack spacing={isSmall ? 8 : 12}>
          <Grid ref={containerRef} container gap={2} justifyContent="space-between">
            <Grid order={{ md: 0, xs: 1 }} size={{ md: 4, xs: 12 }}>
              <TextWithNoahLogo>{textWithIconLabel}</TextWithNoahLogo>
              <Typography gutterBottom variant="headingL">
                {title}
              </Typography>
              <Typography color="text.dark" variant="paragraphBodyL">
                {subtitle}
              </Typography>
            </Grid>
            <Grid order={{ md: 1, xs: 0 }} size={{ md: 6, xs: 12 }}>
              <Box position="relative">
                {image?.src && <StyledImage alt={image?.alt || ''} src={image.src} />}
              </Box>
            </Grid>
          </Grid>
          {isSmall ? (
            <CustomCarousel
              autoPlay
              infiniteLoop
              preventMovementUntilSwipeScrollTolerance
              showIndicators
              swipeable
              showArrows={false}
              showStatus={false}
              showThumbs={false}
              width="100%"
            >
              {cards.map((card) => (
                <Stack key={card.id} alignItems="center" paddingBottom={8} width="100%">
                  <HomepageSmallCard {...card} />
                </Stack>
              ))}
            </CustomCarousel>
          ) : (
            <MotionStack
              direction="row"
              spacing={2.5}
              style={{ opacity, transform }}
              sx={{ position: 'relative', zIndex: 2 }}
            >
              {cards.map((card) => (
                <HomepageSmallCard {...card} key={card.id} />
              ))}
            </MotionStack>
          )}
        </Stack>
      </StyledContainer>
    </StyledSection>
  );
}
